"use client";

import React from "react";
import { MapPin, Phone, Mail, Loader, Loader2, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const contactDetails = [{
  icon: MapPin,
  title: "Adresse",
  value: "Am Alten Rathaus 5a\n34253 Lohfelden"
}, {
  icon: Phone,
  title: "Telefon",
  value: "+49 561 51090 993"
}, {
  icon: Mail,
  title: "E-Mail",
  value: "buchung@kassel-coworking.de"
}];
function ContactFormContent() {
  const {
    isSubmitting,
    handleSubmit,
    control
  } = useWvcForm();
  return <Form onSubmit={handleSubmit} className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
      <FormField control={control} name="name" rules={{
        required: "Dein Name ist erforderlich"
      }} render={({
        field
      }) => <FormItem>
          <FormLabel className="text-[#555555] font-medium">Dein Name</FormLabel>
          <FormControl>
            <Input placeholder="Max Mustermann" className="bg-white border-[#d1d5db] text-[#141414] placeholder:text-[#888888] focus:border-[#FF6F61] focus:ring-[#FF6F61]/20 h-12 transition-all duration-300" style={{
            borderRadius: '8px'
          }} {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>} />

      <FormField control={control} name="email" rules={{
        required: "E‑Mail‑Adresse ist erforderlich",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Bitte geben Sie eine gültige E-Mail-Adresse ein"
        }
      }} render={({
        field
      }) => <FormItem>
          <FormLabel className="text-[#555555] font-medium">E‑Mail‑Adresse</FormLabel>
          <FormControl>
            <Input type="email" placeholder="max@example.com" className="bg-white border-[#d1d5db] text-[#141414] placeholder:text-[#888888] focus:border-[#FF6F61] focus:ring-[#FF6F61]/20 h-12 transition-all duration-300" style={{
            borderRadius: '8px'
          }} {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>} />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
      <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
          <FormLabel className="text-[#555555] font-medium">Telefon</FormLabel>
          <FormControl>
            <Input type="tel" placeholder="+49 123 456789" className="bg-white border-[#d1d5db] text-[#141414] placeholder:text-[#888888] focus:border-[#FF6F61] focus:ring-[#FF6F61]/20 h-12 transition-all duration-300" style={{
            borderRadius: '8px'
          }} {...field} />
          </FormControl>
          <FormDescription className="text-xs text-[#888888]">Optional, falls wir dich anrufen sollen.</FormDescription>
          <FormMessage />
        </FormItem>} />

      <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
          <FormLabel className="text-[#555555] font-medium">Betreff</FormLabel>
          <FormControl>
            <Input placeholder="Allgemeine Anfrage" className="bg-white border-[#d1d5db] text-[#141414] placeholder:text-[#888888] focus:border-[#FF6F61] focus:ring-[#FF6F61]/20 h-12 transition-all duration-300" style={{
            borderRadius: '8px'
          }} {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>} />
    </div>

    <FormField control={control} name="message" rules={{
      required: "Deine Nachricht ist erforderlich"
    }} render={({
      field
    }) => <FormItem>
        <FormLabel className="text-[#555555] font-medium">Deine Nachricht</FormLabel>
        <FormControl>
          <Textarea placeholder="Wie können wir dir helfen?" className="bg-white border-[#d1d5db] text-[#141414] placeholder:text-[#888888] focus:border-[#FF6F61] focus:ring-[#FF6F61]/20 min-h-[150px] resize-none transition-all duration-300 p-4" style={{
          borderRadius: '8px'
        }} {...field} />
        </FormControl>
        <FormMessage />
      </FormItem>} />

    <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-[#141414] hover:bg-[#141414]/90 text-white text-lg font-bold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300" style={{
      borderRadius: '8px'
    }}>
      {isSubmitting ? <>
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Wird gesendet...
      </> : <>
        Jetzt Absenden
        <Send className="ml-2 h-5 w-5" />
      </>}
    </Button>
  </Form>;
}
export default function HowToFindUs() {
  return <section className="bg-[#EEEEEE] ![padding:144px_0px_144px_0px]" id="kontakt">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
      {/* Left: Header + Contact Details */}
      <div className="flex flex-col justify-between">
        <div>
          <span className="text-sm font-semibold uppercase tracking-widest text-[#888888] mb-4 block">Kontakt</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            <span className="text-[#141414]">Get in</span><br />
            <span className="text-[#141414]">Touch.</span>
          </h2>
        </div>
        <div className="space-y-6">
          {contactDetails.map((item, i) => <div key={i} data-index={i} className="flex items-center gap-4">
              <item.icon className="size-5 text-[#888888]" />
              <p className="text-base text-[#141414] font-medium whitespace-pre-line">{item.value}</p>
            </div>)}
          <div className="flex items-center gap-4">
            <Loader className="size-5 text-[#888888]" />
            <p className="text-base text-[#141414] font-medium">
              8-16:30 und nach Vereinbarung
            </p>
          </div>
        </div>
      </div>
{/* Right: Light Contact Form */}
<div className="rounded-[1rem] bg-[#f5f5f5] p-10 md:p-12 flex flex-col">
  <h3 className="font-bold text-[#141414] text-2xl mb-6">Nachricht senden</h3>
  <FormProvider formKey="contact_form_kontakt" formId="contact_form_kontakt" sectionName="contact-us" defaultValues={{
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: false
            },
            subject: {
              required: false
            },
            message: {
              required: true
            }
          }} submitText="Jetzt Absenden" successMessage="Vielen Dank! Wir haben Ihre Nachricht erhalten und werden uns in Kürze bei Ihnen melden." errorMessage="Etwas ist schief gelaufen. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt." className="w-full h-full flex flex-col flex-1">
    <ContactFormContent />
  </FormProvider>
</div>
    </div>
  </div>
</section>;
}